"use client";

import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
// rehypeRaw removed: AI output often contains <module>, <stdin> etc. that get misrendered as HTML
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/cjs/styles/prism";
import { useSettings, themes } from "@/contexts/SettingsContext";

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

// 预处理内容，处理 Claude Code 特有格式
function preprocessContent(content: string): string {
  // 处理文件路径引用格式 file_path:line_number
  // 例如: src/components/App.tsx:42 -> `src/components/App.tsx:42`
  let processed = content.replace(
    /(?<![`\w\/])([a-zA-Z]:[\\\/][^\s:]+:\d+|[.\/]?[\w\-\/\\]+\.[a-zA-Z]+:\d+)(?![`\w])/g,
    '`$1`'
  );

  return processed;
}

// 复制按钮组件
function CopyButton({ code }: { code: string }) {
  const [status, setStatus] = useState<"idle" | "copied" | "failed">("idle");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setStatus("copied");
      setTimeout(() => setStatus("idle"), 2000);
    } catch {
      // 降级方案：使用 execCommand（兼容性更好）
      try {
        const textArea = document.createElement("textarea");
        textArea.value = code;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        setStatus("copied");
        setTimeout(() => setStatus("idle"), 2000);
      } catch {
        setStatus("failed");
        setTimeout(() => setStatus("idle"), 2000);
      }
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="absolute top-2 right-2 px-2 py-1 rounded text-xs transition-all opacity-0 group-hover:opacity-100"
      style={{
        backgroundColor: status === "copied" ? "#22c55e" : status === "failed" ? "#ef4444" : "#374151",
        color: "#e5e7eb",
      }}
    >
      {status === "copied" ? "已复制!" : status === "failed" ? "复制失败" : "复制"}
    </button>
  );
}

export function MarkdownRenderer({ content, className = "" }: MarkdownRendererProps) {
  const { settings } = useSettings();
  const theme = themes[settings.theme];

  // 自定义代码块样式
  const customCodeStyle = {
    ...oneDark,
    'pre[class*="language-"]': {
      ...oneDark['pre[class*="language-"]'],
      margin: 0,
      padding: "1rem",
      borderRadius: "0.5rem",
      fontSize: "0.875rem",
      lineHeight: "1.5",
    },
    'code[class*="language-"]': {
      ...oneDark['code[class*="language-"]'],
      fontSize: "0.875rem",
      lineHeight: "1.5",
    },
  };

  return (
    <div className={`markdown-content overflow-hidden ${className}`} style={{ maxWidth: '100%' }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        // rehypePlugins removed - no raw HTML passthrough needed in chat
        components={{
          // 代码块
          code({ node, inline, className, children, ...props }: any) {
            const match = /language-(\w+)/.exec(className || "");
            const language = match ? match[1] : "";
            const codeString = String(children).replace(/\n$/, "");

            if (!inline && (match || codeString.includes("\n"))) {
              return (
                <div
                  className="relative group my-3 rounded-lg overflow-x-auto"
                  style={{
                    backgroundColor: 'rgba(249, 115, 22, 0.05)',
                    borderLeft: '3px solid #f97316',
                    maxWidth: '100%',
                  }}
                >
                  {language && (
                    <div
                      className="absolute top-0 left-0 px-3 py-1 text-xs"
                      style={{ backgroundColor: "rgba(249, 115, 22, 0.15)", color: "#fb923c" }}
                    >
                      {language}
                    </div>
                  )}
                  <CopyButton code={codeString} />
                  <SyntaxHighlighter
                    style={customCodeStyle as any}
                    language={language || "text"}
                    PreTag="div"
                    customStyle={{
                      margin: 0,
                      borderRadius: 0,
                      paddingTop: language ? "2rem" : "1rem",
                      backgroundColor: 'transparent',
                    }}
                    wrapLongLines={true}
                    {...props}
                  >
                    {codeString}
                  </SyntaxHighlighter>
                </div>
              );
            }

            // 行内代码 - 赛博朋克紫色风格
            return (
              <code
                className="px-1.5 py-0.5 rounded text-sm"
                style={{
                  backgroundColor: "rgba(168, 85, 247, 0.15)",
                  color: "#c084fc",
                  fontFamily: "Consolas, Monaco, 'Courier New', monospace",
                  border: "1px solid rgba(168, 85, 247, 0.3)",
                  textShadow: "0 0 8px rgba(168, 85, 247, 0.4)",
                }}
                {...props}
              >
                {children}
              </code>
            );
          },

          // 段落
          p({ children }) {
            return (
              <p className="mb-3 last:mb-0 leading-relaxed" style={{ color: theme.text }}>
                {children}
              </p>
            );
          },

          // 标题 - 橙黄色下划线
          h1({ children }) {
            return (
              <h1
                className="text-2xl font-bold mb-4 mt-6 first:mt-0 pb-2"
                style={{ color: theme.text }}
              >
                {children}
                <div style={{
                  marginTop: '0.5rem',
                  height: '1px',
                  background: 'linear-gradient(to right, rgba(249, 115, 22, 0.8), rgba(251, 146, 60, 0.6) 50%, transparent)',
                }} />
              </h1>
            );
          },
          h2({ children }) {
            return (
              <h2
                className="text-xl font-bold mb-3 mt-5 first:mt-0 pb-1"
                style={{ color: theme.text }}
              >
                {children}
                <div style={{
                  marginTop: '0.25rem',
                  height: '1px',
                  background: 'linear-gradient(to right, rgba(249, 115, 22, 0.8), rgba(251, 146, 60, 0.6) 50%, transparent)',
                }} />
              </h2>
            );
          },
          h3({ children }) {
            return (
              <h3 className="text-lg font-bold mb-2 mt-4 first:mt-0" style={{ color: theme.text }}>
                {children}
              </h3>
            );
          },
          h4({ children }) {
            return (
              <h4 className="text-base font-bold mb-2 mt-3 first:mt-0" style={{ color: theme.text }}>
                {children}
              </h4>
            );
          },
          h5({ children }) {
            return (
              <h5 className="text-sm font-bold mb-1 mt-2 first:mt-0" style={{ color: theme.text }}>
                {children}
              </h5>
            );
          },
          h6({ children }) {
            return (
              <h6 className="text-sm font-semibold mb-1 mt-2 first:mt-0" style={{ color: theme.textMuted }}>
                {children}
              </h6>
            );
          },

          // 链接 - 橙黄色风格
          a({ href, children }) {
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:opacity-80 transition-opacity"
                style={{ color: "#fb923c" }}
              >
                {children}
              </a>
            );
          },

          // 无序列表 - 橙黄色圆点
          ul({ children }) {
            return (
              <ul className="mb-3 space-y-1 pl-5" style={{ color: theme.text, listStyleType: 'disc' }}>
                {children}
              </ul>
            );
          },

          // 有序列表 - 橙黄色数字
          ol({ children }) {
            return (
              <ol className="mb-3 space-y-1 pl-5" style={{ color: theme.text, listStyleType: 'decimal' }}>
                {children}
              </ol>
            );
          },

          // 列表项
          li({ children }) {
            return <li className="leading-relaxed" style={{ color: theme.text, marker: '#fb923c' }}>{children}</li>;
          },

          // 引用块
          blockquote({ children }) {
            return (
              <blockquote
                className="border-l-4 pl-4 py-1 my-3 italic"
                style={{
                  borderColor: "#6366f1",
                  backgroundColor: theme.bgSecondary,
                  color: theme.textMuted,
                }}
              >
                {children}
              </blockquote>
            );
          },

          // 表格 - 简洁橙黄色风格
          table({ children }) {
            return (
              <div className="overflow-x-auto my-3" style={{ maxWidth: '100%' }}>
                <table
                  className="text-sm"
                  style={{
                    minWidth: 'auto',
                    borderCollapse: 'collapse',
                    border: 'none',
                  }}
                >
                  {children}
                </table>
              </div>
            );
          },
          thead({ children }) {
            return (
              <thead style={{ backgroundColor: 'rgba(249, 115, 22, 0.08)' }}>
                {children}
              </thead>
            );
          },
          tbody({ children }) {
            return <tbody>{children}</tbody>;
          },
          tr({ children }) {
            return (
              <tr className="transition-colors hover:bg-orange-500/10">
                {children}
              </tr>
            );
          },
          th({ children }) {
            return (
              <th
                className="px-4 py-2 text-left font-semibold"
                style={{
                  color: '#fb923c',
                  border: '1px solid rgba(180, 83, 9, 0.5)',
                }}
              >
                {children}
              </th>
            );
          },
          td({ children }) {
            return (
              <td
                className="px-4 py-2"
                style={{
                  color: theme.text,
                  border: '1px solid rgba(180, 83, 9, 0.5)',
                }}
              >
                {children}
              </td>
            );
          },

          // 水平线 - 橙黄色渐变
          hr() {
            return (
              <div
                className="my-4"
                style={{
                  height: '1px',
                  background: 'linear-gradient(to right, transparent, rgba(249, 115, 22, 0.6) 20%, rgba(251, 146, 60, 0.8) 50%, rgba(249, 115, 22, 0.6) 80%, transparent)',
                }}
              />
            );
          },

          // 强调
          strong({ children }) {
            return (
              <strong className="font-bold" style={{ color: theme.text }}>
                {children}
              </strong>
            );
          },
          em({ children }) {
            return (
              <em className="italic" style={{ color: theme.text }}>
                {children}
              </em>
            );
          },

          // 删除线
          del({ children }) {
            return (
              <del style={{ color: theme.textMuted }}>
                {children}
              </del>
            );
          },

          // 图片 - 过滤掉泄漏的 base64 数据（超长 data URI 通常是工具结果泄漏，不是 AI 有意展示）
          img({ src, alt }) {
            if (src && typeof src === 'string' && src.startsWith('data:') && src.length > 1000) {
              return <span className="text-xs opacity-50">[image]</span>;
            }
            return (
              <img
                src={src}
                alt={alt || ""}
                className="max-w-full h-auto rounded-lg my-2"
                style={{ maxHeight: "400px" }}
              />
            );
          },

          // 任务列表项 (GFM)
          input({ type, checked, disabled }) {
            if (type === "checkbox") {
              return (
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  className="mr-2 rounded"
                  style={{ accentColor: "#6366f1" }}
                  readOnly
                />
              );
            }
            return null;
          },
        }}
      >
        {preprocessContent(content)}
      </ReactMarkdown>
    </div>
  );
}
